import { useAddLocationModule, useUpdateLocationModule } from 'api/locations/hooks';
import { moduleNames, modules as storeModules } from 'pages/Stores/components/modules';
import { useLocationArtistAvailableItems } from 'pages/ModulePage/moduleTypes/artists/useArtistAvailableItems';
import { useLocationBannerAvailableItems } from 'pages/ModulePage/moduleTypes/banner/useBannerAvailableItems';
import { useLocationProductAvailableItems } from 'pages/ModulePage/moduleTypes/favoriteProducts/useProductAvailableItems';
import { useLocationLookAvailableItems } from 'pages/ModulePage/moduleTypes/looks/useLookAvailableItems';
import { useLocationVideoAvailableItems } from 'pages/ModulePage/moduleTypes/video/useVideoAvailableItems';
import { useLocationEventAvailableItems } from 'pages/ModulePage/moduleTypes/events/useEventAvailableItems';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { LOCATION, LOCATIONS, RETAILER_LOCATIONS } from 'constants/routes';
import { toastr } from 'utils';
import PageModulePage from './ModulePage';
import { pageModuleTypeKeys, pageModuleTypes } from './pageModuleTypes';

const locationAvailableItemsHooks = {
  [moduleNames.MULTIPLE_LOOK]: useLocationLookAvailableItems,
  [moduleNames.FAVORITE_PRODUCTS]: useLocationProductAvailableItems,
  [moduleNames.BANNER]: useLocationBannerAvailableItems,
  [moduleNames.VIDEO]: useLocationVideoAvailableItems,
  [moduleNames.ARTISTS]: useLocationArtistAvailableItems,
  [moduleNames.EVENTS]: useLocationEventAvailableItems,
};

const LocationModulePage = ({ useLocationQuery, locationDetailRoute }) => {
  const { locationUid, moduleUid } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('ownerPortal', { keyPrefix: 'pageModules' });
  const { t: tApp } = useTranslation('app');
  const { t: tToastr } = useTranslation('toastr');
  const { data: location, isLoading } = useLocationQuery(locationUid);
  const { mutate: addLocationModule, isPending } = useAddLocationModule();
  const { mutate: updateLocationModule, isPending: isUpdatePending } = useUpdateLocationModule();
  const isEditMode = !!moduleUid;

  const backUrl = generatePath(locationDetailRoute, { uid: locationUid });
  const locationsRoute = locationDetailRoute === LOCATION ? LOCATIONS : RETAILER_LOCATIONS;

  const moduleTypes = useMemo(() => {
    const enabledModuleNames = storeModules
      .filter((module) => isEditMode || !module.disabled)
      .map((m) => m.name);

    return Object.fromEntries(
      enabledModuleNames
        .filter((name) => pageModuleTypes[name])
        .map((name) => [name, pageModuleTypes[name](locationAvailableItemsHooks[name])]),
    );
  }, [isEditMode]);

  const existingModule = useMemo(
    () => location?.page_modules?.find((module) => module.uid === moduleUid),
    [location?.page_modules, moduleUid],
  );
  const initialModuleTypeKey = existingModule
    ? pageModuleTypeKeys[existingModule.module_type]
    : null;

  useEffect(() => {
    if (isEditMode && location && !existingModule) {
      navigate(backUrl, { replace: true });
    }
  }, [backUrl, existingModule, isEditMode, location, navigate]);

  const handleSave = async (moduleTypeKey, values, { skipContentUpdate = false } = {}) => {
    const moduleMeta = moduleTypes[moduleTypeKey];
    if (!moduleMeta || !location) return;

    try {
      const content = skipContentUpdate
        ? undefined
        : await moduleMeta.buildPayload(values, { isEditMode });
      const hasTemplateLook =
        moduleTypeKey === moduleNames.MULTIPLE_LOOK &&
        values.selectedItems?.some((item) => item.look_type === 'template');

      if (isEditMode) {
        updateLocationModule(
          {
            locationUid,
            moduleUid,
            moduleType: existingModule?.module_type || moduleMeta.moduleName.toLowerCase(),
            name: values.pageModuleName,
            content,
          },
          {
            onSuccess: () => {
              if (hasTemplateLook) {
                toastr.success(tToastr('success'), tToastr('templateLookCopyInProgress'));
              }
              navigate(backUrl);
            },
          },
        );
        return;
      }

      addLocationModule(
        {
          locationUid,
          moduleType: moduleMeta.moduleName.toLowerCase(),
          name: values.pageModuleName,
          order: location.page_modules?.length || 0,
          content,
        },
        {
          onSuccess: (data) => {
            if (hasTemplateLook) {
              toastr.success(tToastr('success'), tToastr('templateLookCopyInProgress'));
            }
            navigate(backUrl, { state: { scrollToItemId: data.uid } });
          },
        },
      );
    } catch {
      toastr.error(t('failedToCreatePageModule'));
    }
  };

  if (isEditMode && location && !existingModule) {
    return null;
  }

  const currentBreadcrumbLabel = isEditMode ? (
    isLoading ? (
      <Skeleton width={120} />
    ) : (
      existingModule?.name || t('editModule')
    )
  ) : (
    t('createModule')
  );

  return (
    <PageModulePage
      moduleTypes={moduleTypes}
      onSave={handleSave}
      initialModuleTypeKey={initialModuleTypeKey}
      existingModule={existingModule}
      isPending={isPending || isUpdatePending}
      title={t(isEditMode ? 'editModule' : 'createModule')}
      description={t('moduleDetailsSubtitle')}
      saveLabel={t(isEditMode ? 'editModule' : 'createModule')}
      breadcrumbs={[
        { label: tApp('breadcrumbs.locations'), to: locationsRoute },
        {
          label: isLoading ? (
            <Skeleton width={120} />
          ) : (
            location?.name || tApp('breadcrumbs.location')
          ),
          to: backUrl,
        },
        { key: 'page-modules', label: tApp('breadcrumbs.pageModules'), to: backUrl },
        { label: currentBreadcrumbLabel },
      ]}
      loading={isLoading}
      isEditMode={isEditMode}
    />
  );
};

LocationModulePage.propTypes = {
  useLocationQuery: PropTypes.func.isRequired,
  locationDetailRoute: PropTypes.string.isRequired,
};

export default LocationModulePage;
